import { FloatingPortal } from '@floating-ui/react';
import { ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { cn } from 'cn';
import Icon from '../../Icon';
import type { Option } from '../InputCombobox';

interface ComboboxOptionsProps<T> {
  choiceLimit?: number;
  floatingStyles: React.CSSProperties;
  isMultiple: boolean;
  options: Option<T>[];
  optionsClassName?: string;
  optionsMaxHeight?: number;
  selectedValues: T[];
  setFloating: (node: HTMLElement | null) => void;
  theme?: string;
}

export function InputComboboxOptions<T>({
  choiceLimit,
  floatingStyles,
  isMultiple,
  options,
  optionsClassName,
  optionsMaxHeight,
  selectedValues,
  setFloating,
  theme,
}: ComboboxOptionsProps<T>) {
  if (!options.length) return null;

  return (
    <FloatingPortal>
      <ComboboxOptions
        as="ul"
        className={cn(
          'bg-neutral-0 absolute z-120 mt-2 w-fit overflow-y-scroll py-1 shadow-lg focus:ring-0',
          'laane:rounded',
          'kunde:rounded-lg kunde:mt-2 kunde:shadow-lg-dl kunde:p-2',
          optionsClassName,
        )}
        style={{
          ...floatingStyles,
          maxHeight: `${optionsMaxHeight}px`,
        }}
        ref={setFloating}
      >
        {options.map((option, index) => {
          const isOptionSelected = isMultiple
            ? selectedValues.includes(option.value)
            : false;
          const disableDueToLimit =
            isMultiple &&
            !!choiceLimit &&
            selectedValues.length >= choiceLimit &&
            !isOptionSelected;

          return (
            <ComboboxOption
              as="li"
              key={index}
              value={option.value}
              disabled={option.disabled || disableDueToLimit}
              aria-labelledby={`option-${option.value}-label`}
              aria-describedby={
                option.description
                  ? `option-${option.value}-description`
                  : undefined
              }
            >
              {({ active, selected }) => (
                <div
                  className={cn(
                    'laane:typ-ui laane: hover:bg-neutral-5 active:laane:bg-neutral-5 select-none laane:px-4 laane:py-1.5',
                    'kunde:p-3 kunde:typ-body-sm kunde:rounded-sm kunde:flex kunde:items-center',
                    {
                      'text-neutral-60 cursor-default':
                        option.disabled || disableDueToLimit,
                      'cursor-pointer': !option.disabled && !disableDueToLimit,
                      'laane:bg-neutral-5 kunde:bg-blue-5 kunde:text-blue-60':
                        active || selected,
                    },
                  )}
                >
                  <div
                    className={cn('flex gap-3', {
                      'text-neutral-60': option.disabled,
                      'items-center': isMultiple,
                      'items-start': !isMultiple,
                    })}
                  >
                    {isMultiple ? (
                      <span
                        className={cn('checkbox pointer-events-none', {
                          checked: selected,
                        })}
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="flex flex-col">
                      <div id={`option-${option.value}-label`}>
                        {option.label}
                      </div>
                      {option.description ? (
                        <div
                          className="text-neutral-100 typ-ui-sm w-auto leading-2"
                          id={`option-${option.value}-description`}
                        >
                          {option.description}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {selected && theme === 'kunde' && !isMultiple ? (
                    <>
                      <span className="flex-1" />
                      <Icon
                        type="check-circle"
                        className="laane:text-blue-100 kunde:text-blue-60 kunde:min-w-6 kunde:min-h-6"
                        solid
                        size="small"
                      />
                    </>
                  ) : null}
                </div>
              )}
            </ComboboxOption>
          );
        })}
      </ComboboxOptions>
    </FloatingPortal>
  );
}
